import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { useMembers } from "@/contexts/member-context";
import { useMetricsRepository } from "@/pages/tina/hooks/use-metrics-repository";
import { CRON_IDENTITY } from "@/pages/tina/lib/cron-identity";
import type { ScheduledCoverageRow } from "@/types/tina";

import { type CoverageData, buildCoverage } from "./lib";

export interface CoverageState {
  data: CoverageData | null;
  isLoading: boolean;
  isError: boolean;
}

export function useTodayCoverage(): CoverageState {
  const repo = useMetricsRepository();
  const { members } = useMembers();

  // Stable within a calendar day (browser-local = Athens for the curators); the
  // day-scoped query key also refreshes the card across a midnight boundary.
  const dayKey = dayjs().format("YYYY-MM-DD");
  const since = useMemo(() => dayjs().startOf("day").toISOString(), [dayKey]);

  const q = useQuery({
    queryKey: ["tina", "metrics", "today-coverage", dayKey],
    queryFn: () => repo.getTodayCoverage(since),
  });

  // Loading reflects only the coverage query; members are layered in when they
  // arrive, matching use-domination-data.ts.
  const data = useMemo(
    () => (q.data ? buildCoverage(q.data, [...members, CRON_IDENTITY]) : null),
    [q.data, members],
  );

  return { data, isLoading: q.isLoading, isError: q.isError };
}

// What the site groups run on their schedule — the "Copy cities" button's source.
// Changes only when a curator edits a group or a site, so no day-scoped key.
export function useScheduledCoverage(): ScheduledCoverageRow[] | null {
  const repo = useMetricsRepository();
  const q = useQuery({
    queryKey: ["tina", "metrics", "scheduled-coverage"],
    queryFn: () => repo.getScheduledCoverage(),
  });
  return q.data ?? null;
}
